import type { EndEvent, RTCSession } from 'jssip/lib/RTCSession';

import { LogLevel } from 'features/analytics/enums';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { sendAnalytics } from 'features/analytics/store';
import { RtcSessionTracker } from 'features/sip/services';
import { getConnectionChangeTypeByOriginator } from 'features/sip/utils';

// Define the dispatch type based on the createAsyncThunk dispatch
type AsyncThunkDispatch = (action: any) => any;

/**
 * Analytics helper for session tracking events
 */
export const sessionTrackingAnalytics = {
  /**
   * Log when a regular session is accepted and tracked as original
   */
  originalSessionAccepted: (
    dispatch: AsyncThunkDispatch,
    sessionRemoteUri: string,
    session: RTCSession
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.originalSessionAccepted',
        Level: LogLevel.INFO,
        Message: 'Original session accepted and tracked for potential REFER',
        SessionRemoteUri: sessionRemoteUri,
        SessionDirection: session.direction,
        SessionId: session.id,
      })
    );
  },

  /**
   * Log when a teaming session is accepted
   */
  teamingSessionAccepted: (
    dispatch: AsyncThunkDispatch,
    sessionRemoteUri: string,
    session: RTCSession,
    originalSessionUri?: string
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.teamingSessionAccepted',
        Level: LogLevel.INFO,
        Message:
          'Teaming session accepted, configured refer to use original session',
        TeamingSessionUri: sessionRemoteUri,
        TeamingSessionDirection: session.direction,
        TeamingSessionId: session.id,
        OriginalSessionUri: originalSessionUri,
      })
    );
  },

  /**
   * Log when session references are updated
   */
  sessionReferencesUpdated: (
    dispatch: AsyncThunkDispatch,
    operation: string,
    details: Record<string, any>
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.sessionReferencesUpdated',
        Level: LogLevel.INFO,
        Message: `Session references updated: ${operation}`,
        ...details,
      })
    );
  },

  /**
   * Log when all session references are cleaned up
   */
  allSessionsCleanedUp: (dispatch: AsyncThunkDispatch, reason: string) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.allSessionsCleanedUp',
        Level: LogLevel.INFO,
        Message: 'All session references cleared and ensured are disconnected',
        Reason: reason,
      })
    );
  },

  /**
   * Log when a session is terminated
   */
  sessionTerminated: (
    dispatch: AsyncThunkDispatch,
    sessionId: string | undefined,
    reason: string,
    terminateOptions?: any
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.sessionTerminated',
        Level: LogLevel.INFO,
        Message: 'Session terminated',
        SessionId: sessionId,
        Reason: reason,
        TerminateOptions: terminateOptions,
      })
    );
  },

  /**
   * Log when refer session ref is updated
   */
  referSessionUpdated: (
    dispatch: AsyncThunkDispatch,
    newSessionUri: string | null,
    reason: string
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.referSessionUpdated',
        Level: LogLevel.INFO,
        Message: 'Refer session reference updated',
        NewReferSessionUri: newSessionUri,
        Reason: reason,
      })
    );
  },
};

/**
 * Analytics helper for session end events
 */
export const sessionEndAnalytics = {
  /**
   * Log initial session end details
   */
  sessionEndInitiated: (
    dispatch: AsyncThunkDispatch,
    params: {
      event: EndEvent;
      isRefer: boolean;
      isTeaming: boolean;
    }
  ) => {
    const { event, isRefer, isTeaming } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.sessionEndInitiated',
        Level: LogLevel.INFO,
        Message: 'Processing session end event',
        Originator: event.originator,
        Cause: event.cause,
        IsRefer: isRefer,
        IsTeaming: isTeaming,
        EventDebug: {
          messageExists: !!event.message,
          fromExists: !!event.message?.from,
          toExists: !!event.message?.to,
          messageString: event.message?.toString(),
        },
      })
    );
  },

  /**
   * Log when ignoring BYE messages during teaming
   * records the TeamingType (e.g., RemoteRq, RemoteIc, Request, AdhocReq)
   */
  ignoringByeMessage: (
    dispatch: AsyncThunkDispatch,
    teamingType: string | undefined
  ) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk',
        Message:
          'Ignoring BYE message not from svrs-rt endpoint during teaming',
        Level: LogLevel.INFO,
        TeamingType: teamingType,
      })
    );
  },

  /**
   * Log basic session end details
   */
  sessionEnded: (dispatch: AsyncThunkDispatch, event: EndEvent) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEnded',
        Originator: event.originator,
        Cause: event.cause,
        Message: event.message?.toString() ?? '',
        Level: LogLevel.INFO,
      })
    );
  },

  /**
   * Log when completing a teaming call from the teaming flow
   */
  completingTeamingCall: (
    dispatch: AsyncThunkDispatch,
    params: {
      teamingStatus: string | number;
      acceptingUserId?: string | number;
      callId?: string | number;
    }
  ) => {
    const { teamingStatus, acceptingUserId, callId } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.completingTeamingCall',
        Level: LogLevel.INFO,
        Message: 'Completing teaming call',
        TeamingStatus: teamingStatus,
        AcceptingUserId: acceptingUserId,
        CallId: callId,
      })
    );
  },

  /**
   * Log when canceling a teaming request
   */
  cancelingTeamingRequest: (
    dispatch: AsyncThunkDispatch,
    params: {
      teamingStatus: string | number;
      acceptingUserId?: string | number;
      callId?: string | number;
    }
  ) => {
    const { teamingStatus, acceptingUserId, callId } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.cancelingTeamingRequest',
        Level: LogLevel.INFO,
        Message: 'Canceling teaming request',
        TeamingStatus: teamingStatus,
        AcceptingUserId: acceptingUserId,
        CallId: callId,
      })
    );
  },

  /**
   * Log emitting deaf disconnect event
   */
  emitDeafDisconnect: (
    dispatch: AsyncThunkDispatch,
    event: EndEvent,
    isSpecialEndEvent: boolean
  ) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.emitDeafDisconnect',
        Level: LogLevel.INFO,
        Message: 'Emitting deaf disconnect event',
        ConnectionChangeType: getConnectionChangeTypeByOriginator(
          event.originator
        ),
        IsConnectionCallStateNeeded: !isSpecialEndEvent,
      })
    );
  },

  /**
   * Log skipping deaf disconnect
   */
  skipDeafDisconnect: (
    dispatch: AsyncThunkDispatch,
    params: {
      isOriginalReferSession: boolean;
      isRefer: boolean;
      isTeaming: boolean;
      shouldSkipDeafDisconnect: boolean;
    }
  ) => {
    const {
      isOriginalReferSession,
      isRefer,
      isTeaming,
      shouldSkipDeafDisconnect,
    } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.skipDeafDisconnect',
        Level: LogLevel.INFO,
        Message:
          'Skipping deaf disconnect for original REFER session during teaming',
        IsOriginalReferSession: isOriginalReferSession,
        IsRefer: isRefer,
        IsTeaming: isTeaming,
        ShouldSkipDeafDisconnect: shouldSkipDeafDisconnect,
      })
    );
  },

  /**
   * Log session end processing completion
   */
  completed: (
    dispatch: AsyncThunkDispatch,
    params: {
      event: EndEvent;
      isRefer: boolean;
      isOriginalReferSession: boolean;
      isLeaveTeaming: boolean;
      isLeaveHandoff: boolean;
      isTeaming: boolean;
    }
  ) => {
    const {
      event,
      isRefer,
      isOriginalReferSession,
      isLeaveTeaming,
      isLeaveHandoff,
      isTeaming,
    } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.completed',
        Level: LogLevel.INFO,
        Message: 'Session end processing completed',
        ProcessedCause: event.cause,
        ProcessedOriginator: event.originator,
        FinalState: {
          clearedDirectSession: isRefer && isOriginalReferSession,
          completedTeaming: !isLeaveTeaming && !isRefer,
          resetRemoteVideo: !(isRefer && isTeaming),
          performedCleanup: !isLeaveHandoff && !isRefer,
        },
      })
    );
  },
};

/**
 * Centralized analytics for session accepted events (teaming/original)
 */
export function sendSessionAcceptedAnalytics(
  dispatch: AsyncThunkDispatch,
  sessionRole: string | undefined,
  currentSession: RTCSession
) {
  const sessionRemoteUri =
    currentSession.remote_identity?.uri?.toString() || '';

  if (sessionRole === 'teaming') {
    sessionTrackingAnalytics.teamingSessionAccepted(
      dispatch,
      sessionRemoteUri,
      currentSession,
      RtcSessionTracker.getDirectSession()?.remote_identity?.uri?.toString()
    );
  } else if (sessionRole === 'direct') {
    sessionTrackingAnalytics.originalSessionAccepted(
      dispatch,
      sessionRemoteUri,
      currentSession
    );
  } else {
    // Fallback: unknown session role
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.unknownSessionRole',
        Level: LogLevel.WARNING,
        Message: 'Session accepted with unknown role',
        SessionRemoteUri: sessionRemoteUri,
        SessionId: currentSession.id,
      })
    );
  }
}

/**
 * Centralized analytics for session disconnection events
 */
export function sendSessionDisconnectedAnalytics(
  dispatch: AsyncThunkDispatch,
  sessionRole: 'teaming' | 'direct' | 'wavello' | undefined,
  currentSession: RTCSession
) {
  const sessionRemoteUri =
    currentSession.remote_identity?.uri?.toString() || '';

  let method = '';
  let message = '';
  let level = LogLevel.INFO;
  switch (sessionRole) {
    case 'teaming':
      method = 'sessionTrackingAnalytics.teamingSessionDisconnected';
      message = 'Teaming session guaranteed disconnected and cleaned up';
      break;
    case 'direct':
      method = 'sessionTrackingAnalytics.directSessionDisconnected';
      message = 'Direct session guaranteed disconnected and cleaned up';
      break;
    case 'wavello':
      method = 'sessionTrackingAnalytics.wavelloSessionDisconnected';
      message = 'Wavello session guaranteed disconnected and cleaned up';
      break;
    default:
      method = 'sessionTrackingAnalytics.unknownSessionRole';
      message = 'Session disconnected with unknown role';
      level = LogLevel.WARNING;
      break;
  }
  dispatch(
    sendAnalyticsInfo({
      Method: method,
      Level: level,
      Message: message,
      SessionRemoteUri: sessionRemoteUri,
      SessionDirection: currentSession.direction,
      SessionId: currentSession.id,
      ConnectionState: currentSession.connection?.connectionState,
    })
  );
}

/**
 * Analytics for when we force close a session that wasn't properly disconnected
 */
export function sendSessionForceClosedAnalytics({
  dispatch,
  sessionRole,
  oldSession,
  newSession,
}: {
  dispatch: AsyncThunkDispatch;
  sessionRole: string;
  oldSession: RTCSession;
  newSession: RTCSession;
}) {
  const oldSessionUri = oldSession.remote_identity?.uri?.toString() || '';
  const newSessionUri = newSession.remote_identity?.uri?.toString() || '';

  dispatch(
    sendAnalyticsInfo({
      Method: 'sessionTrackingAnalytics.sessionForceClosed',
      Level: LogLevel.WARNING,
      Message: `Forced disconnection of ${sessionRole} session that was not properly closed during session replacement`,
      SessionRole: sessionRole,
      OldSessionUri: oldSessionUri,
      OldSessionId: oldSession.id,
      OldConnectionState: oldSession.connection?.connectionState,
      OldSignalingState: oldSession.connection?.signalingState,
      NewSessionUri: newSessionUri,
      NewSessionId: newSession.id,
      Reason: 'Session replacement detected incompletely disconnected session',
    })
  );
}

/**
 * Track analytics when all RTC sessions are disconnected and cleared
 */
export function trackAllSessionsDisconnectedAnalytics(
  dispatch: AsyncThunkDispatch,
  directSessionUri: string,
  teamingSessionUri: string,
  nonActiveSessionCount: number
) {
  dispatch(
    sendAnalytics({
      Method: 'RtcSessionTracker.disconnectAndClearAll',
      Level: LogLevel.INFO,
      Message: 'All RTC sessions disconnected and cleared',
      DirectSessionUri: directSessionUri || 'none',
      TeamingSessionUri: teamingSessionUri || 'none',
      NonActiveSessionCount: nonActiveSessionCount,
    })
  );
}
